/**
 * EditorScene - Main Phaser scene for the Map Editor
 * Handles canvas rendering, input, and map manipulation
 */

import Phaser from 'phaser';
import { EditorUI } from '../editor/EditorUI.js';
import { Actions } from '../editor/HistoryManager.js';

export default class EditorScene extends Phaser.Scene {
  constructor() {
    super({ key: 'EditorScene' });

    // Map data
    this.mapData = null;
    this.tileSize = 32;

    // Tile layers (Phaser tilemaps)
    this.layers = {
      background: null,
      collision: null,
      decoration: null,
    };

    // Entity sprites
    this.entitySprites = [];

    // Grid graphics
    this.gridGraphics = null;
    this.showGrid = true;

    // Selection
    this.selectedTile = null;
    this.selectedTileIndex = 0;
    this.selectedEntity = null;
    this.currentTool = 'paint';
    this.currentLayer = 'background';

    // Painting state
    this.isPainting = false;
    this.lastPaintedTile = null;

    // Camera controls
    this.isDragging = false;
    this.dragStart = { x: 0, y: 0 };

    // UI reference
    this.ui = null;

    // Loaded tileset images
    this.tilesetImages = {};
    this.tileIndexMap = {}; // Maps tile index to { texture, frame }
  }

  preload() {
    // Load all tile images
    this.load.image('road_tiles', '/assets/tiles/road_tiles.png');
    this.load.image('sidewalk_tiles', '/assets/tiles/sidewalk_tiles.png');
    this.load.image('building_ruins', '/assets/tiles/building_ruins.png');
    this.load.image('burned_vehicles', '/assets/tiles/burned_vehicles.png');
    this.load.image('barricades', '/assets/tiles/barricades.png');
    this.load.image('urban_debris', '/assets/tiles/urban_debris.png');
    this.load.image('dead_trees', '/assets/tiles/dead_trees.png');
    this.load.image('blood_decals', '/assets/tiles/blood_decals.png');
    this.load.image('manholes', '/assets/tiles/manholes.png');
    this.load.image('neon_signs', '/assets/tiles/neon_signs.png');

    // Load entity sprites
    this.load.image('tank', '/assets/sprites/player/tank.png');
    this.load.image('scientist', '/assets/sprites/npcs/scientist.png');
  }

  create() {
    // Set world bounds (will be updated when map is loaded)
    this.cameras.main.setBounds(0, 0, 800, 600);

    // Create UI
    this.ui = new EditorUI(this);

    // Create grid graphics
    this.gridGraphics = this.add.graphics();
    this.gridGraphics.setDepth(1000);

    // Create layer containers
    this.layerContainers = {
      background: this.add.container(0, 0),
      collision: this.add.container(0, 0),
      decoration: this.add.container(0, 0),
      entities: this.add.container(0, 0),
    };

    // Set depths
    this.layerContainers.background.setDepth(0);
    this.layerContainers.collision.setDepth(100);
    this.layerContainers.decoration.setDepth(200);
    this.layerContainers.entities.setDepth(300);

    // Setup input
    this.setupInput();

    // Check for auto-save recovery
    this.ui.checkAutoSaveRecovery();

    // Create a default empty map if none loaded
    if (!this.mapData) {
      this.ui.createNewMap('Untitled', 25, 19);
    }
  }

  setupInput() {
    // Mouse/pointer input for painting and camera control
    this.input.on('pointerdown', (pointer) => {
      if (pointer.middleButtonDown()) {
        // Middle-click: start camera drag
        this.isDragging = true;
        this.dragStart.x = pointer.x;
        this.dragStart.y = pointer.y;
      } else if (pointer.rightButtonDown()) {
        // Right-click: start camera drag
        this.isDragging = true;
        this.dragStart.x = pointer.x;
        this.dragStart.y = pointer.y;
      } else if (pointer.leftButtonDown()) {
        // Left-click: paint/interact
        this.isPainting = true;
        this.handlePaint(pointer);
      }
    });

    this.input.on('pointermove', (pointer) => {
      if (this.isDragging) {
        // Camera drag
        const dx = this.dragStart.x - pointer.x;
        const dy = this.dragStart.y - pointer.y;
        this.cameras.main.scrollX += dx;
        this.cameras.main.scrollY += dy;
        this.dragStart.x = pointer.x;
        this.dragStart.y = pointer.y;
      } else if (this.isPainting) {
        // Continue painting
        this.handlePaint(pointer);
      }

      // Update cursor position in UI
      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      const tileX = Math.floor(worldPoint.x / this.tileSize);
      const tileY = Math.floor(worldPoint.y / this.tileSize);
      this.ui.updateCursorPosition(tileX, tileY);
    });

    this.input.on('pointerup', () => {
      this.isPainting = false;
      this.isDragging = false;
      this.lastPaintedTile = null;
    });

    // Scroll wheel for zoom
    this.input.on('wheel', (pointer, gameObjects, deltaX, deltaY) => {
      const zoom = this.cameras.main.zoom;
      const newZoom = Phaser.Math.Clamp(zoom - deltaY * 0.001, 0.5, 2);
      this.cameras.main.setZoom(newZoom);
    });
  }

  handlePaint(pointer) {
    if (!this.mapData) return;

    const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const tileX = Math.floor(worldPoint.x / this.tileSize);
    const tileY = Math.floor(worldPoint.y / this.tileSize);

    // Check bounds
    if (tileX < 0 || tileX >= this.mapData.width ||
        tileY < 0 || tileY >= this.mapData.height) {
      return;
    }

    // Avoid repainting the same tile
    const tileKey = `${tileX},${tileY}`;
    if (this.lastPaintedTile === tileKey) return;
    this.lastPaintedTile = tileKey;

    if (this.currentLayer === 'entities') {
      this.handleEntityPlacement(tileX, tileY);
    } else {
      this.handleTilePlacement(tileX, tileY);
    }
  }

  handleTilePlacement(tileX, tileY) {
    const layer = this.currentLayer;
    const oldTileIndex = this.mapData.layers[layer][tileY][tileX];
    let newTileIndex;

    switch (this.currentTool) {
      case 'paint':
        if (this.selectedTileIndex === null) return;
        newTileIndex = this.selectedTileIndex;
        break;

      case 'erase':
        newTileIndex = 0;
        break;

      case 'fill':
        this.floodFill(tileX, tileY, layer, this.selectedTileIndex || 0);
        return;

      case 'select':
        // Select tool doesn't paint
        return;

      default:
        return;
    }

    if (oldTileIndex === newTileIndex) return;

    // Create action and push to history
    const action = Actions.placeTile(
      this.mapData,
      layer,
      tileX,
      tileY,
      oldTileIndex,
      newTileIndex,
      (l, x, y, idx) => this.updateTileVisual(l, x, y, idx)
    );

    action.redo(); // Apply immediately
    this.ui.history.push(action);
    this.ui.markUnsavedChanges();
  }

  handleEntityPlacement(tileX, tileY) {
    if (this.currentTool === 'erase') {
      // Find and remove entity at position
      const entity = this.mapData.entities.find(
        e => Math.floor(e.x / this.tileSize) === tileX &&
             Math.floor(e.y / this.tileSize) === tileY
      );
      if (entity) {
        const action = Actions.removeEntity(
          this.mapData.entities,
          entity,
          (type, e) => this.updateEntityVisual(type, e)
        );
        action.redo();
        this.ui.history.push(action);
        this.ui.markUnsavedChanges();
      }
    } else if (this.currentTool === 'paint' && this.selectedEntity) {
      // Place new entity
      const entity = {
        id: `entity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: this.selectedEntity,
        x: tileX * this.tileSize + this.tileSize / 2,
        y: tileY * this.tileSize + this.tileSize / 2,
        properties: {},
      };

      const action = Actions.placeEntity(
        this.mapData.entities,
        entity,
        (type, e) => this.updateEntityVisual(type, e)
      );
      action.redo();
      this.ui.history.push(action);
      this.ui.markUnsavedChanges();
    }
  }

  floodFill(startX, startY, layer, newTileIndex) {
    const targetTileIndex = this.mapData.layers[layer][startY][startX];
    if (targetTileIndex === newTileIndex) return;

    const changes = [];
    const visited = new Set();
    const queue = [{ x: startX, y: startY }];

    while (queue.length > 0) {
      const { x, y } = queue.shift();
      const key = `${x},${y}`;

      if (visited.has(key)) continue;
      if (x < 0 || x >= this.mapData.width || y < 0 || y >= this.mapData.height) continue;
      if (this.mapData.layers[layer][y][x] !== targetTileIndex) continue;

      visited.add(key);
      changes.push({
        x, y,
        oldTileIndex: targetTileIndex,
        newTileIndex: newTileIndex,
      });

      queue.push({ x: x + 1, y });
      queue.push({ x: x - 1, y });
      queue.push({ x, y: y + 1 });
      queue.push({ x, y: y - 1 });
    }

    if (changes.length === 0) return;

    const action = Actions.placeMultipleTiles(
      this.mapData,
      layer,
      changes,
      (l, x, y, idx) => this.updateTileVisual(l, x, y, idx)
    );
    action.redo();
    this.ui.history.push(action);
    this.ui.markUnsavedChanges();
  }

  updateTileVisual(layer, x, y, tileIndex) {
    const container = this.layerContainers[layer];
    const key = `${x},${y}`;

    // Remove existing tile sprite at this position (filter first to avoid mutation during iteration)
    const toRemove = container.list.filter(child => child.getData('key') === key);
    toRemove.forEach(child => child.destroy());

    // Add new tile sprite if not empty (index 0 = empty)
    if (tileIndex > 0 && this.ui.tiles[tileIndex]) {
      const tile = this.ui.tiles[tileIndex];
      const sprite = this.add.image(
        x * this.tileSize + this.tileSize / 2,
        y * this.tileSize + this.tileSize / 2,
        this.getTextureKeyFromPath(tile.path)
      );
      sprite.setDisplaySize(this.tileSize, this.tileSize);
      sprite.setData('key', key);

      // Collision layer visualization
      if (layer === 'collision') {
        sprite.setTint(0xff4444);
        sprite.setAlpha(0.5);
      }

      container.add(sprite);
    }
  }

  updateEntityVisual(type, entity) {
    const container = this.layerContainers.entities;

    if (type === 'remove') {
      // Filter first to avoid mutation during iteration
      const toRemove = container.list.filter(child => child.getData('entityId') === entity.id);
      toRemove.forEach(child => child.destroy());
    } else if (type === 'add') {
      const sprite = this.createEntitySprite(entity);
      container.add(sprite);
    }
  }

  createEntitySprite(entity) {
    let textureKey = 'tank'; // default
    let scale = 0.1;
    let tint = 0xffffff;

    switch (entity.type) {
      case 'player_spawn':
        textureKey = 'tank';
        tint = 0x39ff14;
        break;
      case 'zombie_shambler':
        textureKey = 'tank';
        tint = 0x888800;
        scale = 0.08;
        break;
      case 'zombie_crawler':
        textureKey = 'tank';
        tint = 0x666600;
        scale = 0.06;
        break;
      case 'boss':
        textureKey = 'tank';
        tint = 0xff0000;
        scale = 0.15;
        break;
      case 'scientist':
        textureKey = 'scientist';
        scale = 0.08;
        break;
      case 'survivor':
        textureKey = 'scientist';
        tint = 0x00ffff;
        scale = 0.08;
        break;
      case 'enemy_spawn':
        textureKey = 'tank';
        tint = 0xff00ff;
        scale = 0.05;
        break;
    }

    const sprite = this.add.image(entity.x, entity.y, textureKey);
    sprite.setScale(scale);
    sprite.setTint(tint);
    sprite.setData('entityId', entity.id);
    sprite.setData('entityType', entity.type);

    return sprite;
  }

  getTextureKeyFromPath(path) {
    // Convert path like "/assets/tiles/road_tiles.png" to texture key "road_tiles"
    const filename = path.split('/').pop();
    return filename.replace('.png', '');
  }

  loadMapData(mapData) {
    this.mapData = mapData;
    this.tileSize = mapData.tile_size || 32;

    // Update world bounds
    const worldWidth = mapData.width * this.tileSize;
    const worldHeight = mapData.height * this.tileSize;
    this.cameras.main.setBounds(0, 0, worldWidth, worldHeight);
    this.cameras.main.centerOn(worldWidth / 2, worldHeight / 2);

    // Clear existing content
    Object.values(this.layerContainers).forEach(container => {
      container.removeAll(true);
    });

    // Render map layers
    this.renderMapLayers();

    // Render entities
    this.renderEntities();

    // Draw grid
    this.drawGrid();
  }

  renderMapLayers() {
    const layers = ['background', 'collision', 'decoration'];

    layers.forEach(layerName => {
      const layerData = this.mapData.layers[layerName];
      if (!layerData) return;

      for (let y = 0; y < this.mapData.height; y++) {
        for (let x = 0; x < this.mapData.width; x++) {
          const tileIndex = layerData[y][x];
          if (tileIndex > 0) {
            this.updateTileVisual(layerName, x, y, tileIndex);
          }
        }
      }
    });
  }

  renderEntities() {
    if (!this.mapData.entities) return;

    const container = this.layerContainers.entities;
    this.mapData.entities.forEach(entity => {
      const sprite = this.createEntitySprite(entity);
      container.add(sprite);
    });
  }

  drawGrid() {
    this.gridGraphics.clear();

    if (!this.showGrid || !this.mapData) return;

    this.gridGraphics.lineStyle(1, 0x39ff14, 0.2);

    const width = this.mapData.width * this.tileSize;
    const height = this.mapData.height * this.tileSize;

    // Vertical lines
    for (let x = 0; x <= this.mapData.width; x++) {
      this.gridGraphics.moveTo(x * this.tileSize, 0);
      this.gridGraphics.lineTo(x * this.tileSize, height);
    }

    // Horizontal lines
    for (let y = 0; y <= this.mapData.height; y++) {
      this.gridGraphics.moveTo(0, y * this.tileSize);
      this.gridGraphics.lineTo(width, y * this.tileSize);
    }

    this.gridGraphics.strokePath();
  }

  toggleGrid() {
    this.showGrid = !this.showGrid;
    this.drawGrid();
  }

  // UI callback methods
  onTileSelected(tile, index) {
    this.selectedTile = tile;
    this.selectedTileIndex = index;
  }

  onEntitySelected(entityType) {
    this.selectedEntity = entityType;
    this.selectedTile = null;
  }

  onToolChanged(tool) {
    this.currentTool = tool;
  }

  onLayerChanged(layer) {
    this.currentLayer = layer;
  }

  onLayerVisibilityChanged(layer, visible) {
    if (this.layerContainers[layer]) {
      this.layerContainers[layer].setVisible(visible);
    }
  }

  update() {
    // Update logic if needed
  }

  shutdown() {
    if (this.ui) {
      this.ui.destroy();
    }
  }
}
